import { DateTime } from 'luxon'
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useLiveQuery } from 'dexie-react-hooks'

import { offlineDb, type SyncIssue } from '../../offline/db'
import { SyncStatus } from '../../DTOs/Sync/SyncDtos'
import { cachedMachineName, describeShiftId } from '../../offline/describeShiftId'
import { entityLabel } from '../../offline/entityLabels'
import Button from '../Buttons/Button'
import i18n from '../../i18n'

function issueContext(issue: SyncIssue): string {
  const row = (issue.clientData ?? issue.serverState ?? {}) as {
    plantId?: number
    truckId?: number
    plant?: { plantRef?: string }
    loads?: number
    hour?: number
    startTime?: unknown
  }

  const machine = row.plant?.plantRef ?? cachedMachineName(row.plantId ?? row.truckId)
  const shift = describeShiftId(issue.shiftId)
  const detail = row.loads != null ? i18n.t('syncIssues.loads', { count: row.loads }) : undefined
  const hour = row.hour != null ? i18n.t('syncIssues.hour', { hour: row.hour }) : undefined
  const logged = issue.createdAt ? DateTime.fromMillis(issue.createdAt).toFormat('HH:mm') : undefined

  const parts = [machine, detail, hour, shift, logged && i18n.t('syncIssues.logged', { time: logged })].filter(Boolean)
  return parts.length ? i18n.t('syncIssues.about', { parts: parts.join(', ') }) : ''
}

function issueLabel(issue: SyncIssue): string {
  const entityName = entityLabel(issue.entity)
  const about = issueContext(issue)
  if (issue.status === SyncStatus.Conflict) return i18n.t('syncIssues.conflict', { entity: entityName, about })
  if (issue.status === SyncStatus.ShiftLocked) return i18n.t('syncIssues.shiftLocked', { entity: entityName, about })
  if (issue.status === SyncStatus.Error) return i18n.t('syncIssues.failed', { entity: entityName, about, reason: typeof issue.serverState === 'string' ? issue.serverState : i18n.t('syncIssues.serverError') })
  return i18n.t('syncIssues.notFound', { entity: entityName, about })
}

async function dismissIssue(id: number) {
  await offlineDb.syncIssues.delete(id)
}

async function dismissAll() {
  await offlineDb.syncIssues.clear()
}

interface SyncIssuesModalProps {
  onClose: () => void
  onResolveConflict: (issue: SyncIssue) => void
}

const SyncIssuesModal = ({ onClose, onResolveConflict }: SyncIssuesModalProps) => {
  const issues = useLiveQuery(() => offlineDb.syncIssues.orderBy('createdAt').toArray(), []) ?? []

  return (
    <div>
      <h2 className="border-b border-theme-border font-semibold pb-2 mb-8 text-[1.6rem] text-theme-text">
        <FontAwesomeIcon icon={faTriangleExclamation} className="mr-3 text-theme-danger" />
        {i18n.t('status.syncIssues', { count: issues.length })}
      </h2>

      {issues.length === 0 ? (
        <p className="!text-theme-text">{i18n.t('syncIssues.none')}</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {issues.map(issue => (
            <li
              key={issue.id}
              className="flex items-start justify-between gap-3 border-b border-theme-border pb-2 last:border-b-0"
            >
              <span className="text-sm text-theme-text">{issueLabel(issue)}</span>
              <div className="flex items-center gap-2 shrink-0">
                {issue.status === SyncStatus.Conflict && (
                  <Button variant="primary" size="sm" onClick={() => onResolveConflict(issue)}>{i18n.t('actions.resolve')}</Button>
                )}
                <Button variant="secondary" size="sm" onClick={() => dismissIssue(issue.id!)}>{i18n.t('actions.dismiss')}</Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <div className="flex justify-end gap-2 mt-6">
        {issues.length > 1 && (
          <Button variant="danger" onClick={dismissAll}>{i18n.t('actions.dismissAll')}</Button>
        )}
        <Button variant="secondary" onClick={onClose}>{i18n.t('actions.close')}</Button>
      </div>
    </div>
  )
}

export default SyncIssuesModal
